import React, { FC, useState } from "react";

import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { Chips, ChipsChangeEvent } from "primereact/chips";
import { Divider } from "primereact/divider";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DialIndicatorEditorProps, DialIndicatorProps } from "./DialIndicatorTypes";

interface CustomTick {
  value: number | string;
  overriddenValue?: string | number;
}

const getAllTicks = (config: DialIndicatorProps): CustomTick[] => {
  const manualTicks = config.ticks ?? [];
  const manualTickValues = manualTicks.map(t => t.value);
  const zoneTickValues = config.zones?.map(zone => zone.maxValue) ?? [];

  const allTickValues: (number | string)[] = [...manualTickValues, ...zoneTickValues];

  const uniqueTickValues = Array.from(new Set(allTickValues));

  const sortedTickValues = uniqueTickValues.sort((a, b) => {
    if (typeof a === "number" && typeof b === "number") return a - b;
    if (typeof a === "number") return -1;
    if (typeof b === "number") return 1;
    return String(a).localeCompare(String(b));
  });

  const mergedTicks: CustomTick[] = sortedTickValues.map(value => {
    const fromTick = manualTicks.find(t => t.value === value);
    const fromZone = config.zones?.find(z => z.maxValue === value);
    const overridden = fromTick?.overriddenValue ?? fromZone?.overriddenValue;

    return {
      value,
      ...(overridden !== undefined ? { overriddenValue: overridden } : {}),
    };
  });

  return mergedTicks;
};

const DialIndicatorTicksEditor: FC<DialIndicatorEditorProps> = ({ config, onConfigChange }) => {
  const [ticks, setTicks] = useState<CustomTick[]>(getAllTicks(config));

  const handleTicksChange = (e: ChipsChangeEvent) => {
    const currentTicks =
      (e.value ?? [])
        .map((v: string) => {
          const parsed = parseFloat(v);
          return Number.isNaN(parsed) ? null : { value: parsed };
        })
        .filter((tick): tick is { value: number } => tick !== null) ?? [];

    const newCurrentConfig = {
      ...config,
      ticks: currentTicks,
    };

    onConfigChange(newCurrentConfig);
    setTicks(getAllTicks(newCurrentConfig));
  };

  const handleRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const { newData } = e;
    const { value, overriddenValue } = newData;

    const updatedConfig = { ...config };

    if (typeof value === "number" && (config.ticks ?? []).some(t => t.value === value)) {
      updatedConfig.ticks = (config.ticks ?? []).map(t =>
        t.value === value ? { ...t, overriddenValue } : t
      );
    } else {
      updatedConfig.zones = (config.zones ?? []).map(zone => {
        if (zone.maxValue === value) {
          return {
            ...zone,
            overriddenValue,
          };
        }
        return zone;
      });
    }

    onConfigChange(updatedConfig);
    setTicks(getAllTicks(updatedConfig));
  };

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.value || ""}
        onChange={e => options.editorCallback?.(e.target.value)}
        style={{ width: "100%" }}
      />
    );
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-row justify-content-between">
        <div className="flex flex-column gap-2">
          <label htmlFor="ticks">{T("word.ticks")}</label>
          <Chips
            id="ticks"
            value={(config.ticks ?? []).map(t => String(t.value))}
            onChange={e => handleTicksChange(e)}
          />
        </div>
        <div className="flex flex-row gap-3">
          <div className="flex flex-column gap-2">
            <label htmlFor="hideTicks">{T("action.hide-ticks")}</label>
            <InputSwitch
              id="hideTicks"
              checked={config.hideTicks ?? false}
              onChange={e => onConfigChange({ ...config, hideTicks: e.value })}
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="ticksFontSize">{T("word.ticks-font-size")}</label>
            <div className="p-inputgroup w-full">
              <InputNumber
                id="ticksFontSize"
                value={config.ticksFontSize ?? 15}
                onValueChange={e => onConfigChange({ ...config, ticksFontSize: e.value ?? 0 })}
              />
              <span className="p-inputgroup-addon">px</span>
            </div>
          </div>
        </div>
      </div>
      <Divider />
      <DataTable value={ticks} onRowEditComplete={handleRowEditComplete} editMode="row">
        <Column
          field="value"
          header={T("word.value")}
          body={rowData => String(rowData.value)}
          style={{ width: "45%" }}
        />
        <Column
          field="overriddenValue"
          header={T("label.overridden-value")}
          body={rowData => String(rowData.overriddenValue ?? "")}
          style={{ width: "40%" }}
          editor={options => textEditor(options)}
        />
        <Column rowEditor style={{ width: "15%", textAlign: "end" }} />
      </DataTable>
    </div>
  );
};

export default DialIndicatorTicksEditor;
